import { useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

const WORDPRESS_URL = "https://www.mostwanted.ro";
const BASENAME = "/emergent-landing";

// Team data
const team = [
  {
    name: "Gabriela Konhäusner",
    role: "Interior Designer | Home Specialist",
    image: "https://www.mostwanted.ro/wp-content/uploads/2023/06/Gabriela-Postole-interior-designer_03-1024x1024.jpg",
    bio: "Designer de interior cu experienta in toate etapele de design. Abordare holistica a proiectelor. Perspectiva mea este tehnica, cu o nota artistica adaugata care creeaza un echilibru bun."
  },
  {
    name: "Horváth Noémi",
    role: "Interior Designer | 3D Expert",
    image: "https://www.mostwanted.ro/wp-content/uploads/2016/11/noemi.jpg",
    bio: "Transform proiecte in spatii uimitoare. Vizualizari 3D, atenta la detalii si o pasiune pentru creativitate, aduc o nota unica fiecarui proiect."
  },
  {
    name: "Fülöp József",
    role: "Interior Designer | Commercial Design",
    image: "https://www.mostwanted.ro/wp-content/uploads/2016/11/04.jpg",
    bio: "Cu o vasta experienta in proiectarea diferitelor spatii comerciale aduc expertiza fiecarui proiect. Combin caracterul practic si functionalitatea."
  },
  {
    name: "Balla Boglárka",
    role: "Interior Designer | Commercial Design",
    image: "https://www.mostwanted.ro/wp-content/uploads/2025/03/Boglarka-1024x1024.jpg",
    bio: "Motivatia mea este de a obtine cele mai bune rezultate din fiecare proiect si de a modela spatiile in functie de cerintele clientului."
  }
];

const values = [
  { title: "Misiune", text: "Cream spatii functionale si estetice, reflectand personalitatea utilizatorilor, fara a compromite ergonomia." },
  { title: "Viziune", text: "Cautam echilibrul intre forma si functionalitate, aplicand designuri simple si eficiente." },
  { title: "Abordare", text: "Adaptam fiecare proiect la nevoile, gusturile si asteptarile unice ale clientilor nostri." }
];

const testimonials = [
  { name: "Vlad Florea", company: "Trend Furniture", text: "Colaborarea cu Most Wanted Design pe proiectele clientilor nostri a fost una excelenta. Studioul demonstreaza o viziune holistica in design." },
  { name: "Daniel Cosarca", text: "Very good services. With a very well-developed aesthetic eye, and working with teams of professionals, it was a very pleasant experience." },
  { name: "Sebastian Schapitz", text: "Very nice contact combined with best work. Always happy!" }
];

// Animation
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? "visible" : "hidden"} variants={staggerContainer} className={className}>
      {children}
    </motion.div>
  );
};

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: "Acasă", url: "https://www.mostwanted.ro/" },
    { name: "Despre Noi", url: "https://www.mostwanted.ro/about/" }
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        page="about" 
        breadcrumbs={breadcrumbs}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" className="font-['Cormorant_Garamond'] text-xl md:text-2xl tracking-wide text-[#1a1a1a]">
              MOST <span className="font-normal">WANTED</span> DESIGN
            </Link>
            <Link to="/" className="flex items-center gap-2 font-['Manrope'] text-sm uppercase tracking-widest text-[#1a1a1a] hover:text-[#737373] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Inapoi
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div className="absolute -left-10 top-20 w-[120px] h-[500px] bg-[#1a1a1a] transform -rotate-[25deg] hidden md:block" />
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.3em] text-[#737373] mb-4">
              Despre Noi
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-4xl md:text-6xl lg:text-7xl text-[#1a1a1a] leading-tight max-w-4xl">
              Cream spatii cu scop, punem accent pe functionalitate
            </motion.h1>
          </AnimatedSection>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-[#f8f8f8]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Image */}
            <AnimatedSection>
              <motion.div variants={fadeInUp} className="relative">
                <div className="absolute -left-6 top-1/4 w-[80px] h-[350px] bg-[#1a1a1a] transform -rotate-[25deg] hidden md:block" />
                <img
                  src="https://www.mostwanted.ro/wp-content/uploads/2023/01/01.jpg"
                  alt="Most Wanted Design Studio"
                  className="w-full aspect-[4/3] object-cover"
                />
              </motion.div>
            </AnimatedSection>

            {/* Content */}
            <AnimatedSection>
              <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#1a1a1a] mb-6">
                Suntem o Firma de design, Most Wanted, Cluj
              </motion.h2>
              <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed mb-8">
                Cu aproape 20 de ani de experienta, echipa noastra este pregatita sa abordeze orice provocare in designul de interior. Am lucrat la proiecte rezidentiale si comerciale, inclusiv apartamente, case si restaurante, in Romania si Europa de Est.
              </motion.p>
              <motion.p variants={fadeInUp} className="font-['Manrope'] text-[#737373] leading-relaxed">
                Ne dedicam sa gasim solutii estetice si practice care sa raspunda nevoilor tale.
              </motion.p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatedSection>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {values.map((value, i) => (
                <motion.div
                  key={value.title}
                  variants={fadeInUp}
                  className="p-8 border border-[#e5e5e5] hover:border-[#1a1a1a] transition-colors duration-500"
                >
                  <div className="w-12 h-12 border border-[#1a1a1a] flex items-center justify-center mb-6">
                    <span className="font-['Cormorant_Garamond'] text-xl text-[#1a1a1a]">{i + 1}</span>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#1a1a1a] mb-4">{value.title}</h3>
                  <p className="font-['Manrope'] text-[#737373] leading-relaxed">{value.text}</p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-[#f8f8f8]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.3em] text-[#737373] mb-4">
              Echipa Noastra
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] mb-16">
              Oamenii din spatele proiectelor
            </motion.h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-square object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                  {/* Hover overlay with bio */}
                  <div className="absolute inset-0 bg-[#1a1a1a]/90 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center p-6">
                    <p className="font-['Manrope'] text-sm text-white/80 text-center leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl text-[#1a1a1a] mb-1">{member.name}</h3>
                <p className="font-['Manrope'] text-sm text-[#737373]">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-2">20+</p>
              <p className="font-['Manrope'] text-sm uppercase tracking-widest text-white/50">Ani Experienta</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-2">150+</p>
              <p className="font-['Manrope'] text-sm uppercase tracking-widest text-white/50">Proiecte</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-2">4</p>
              <p className="font-['Manrope'] text-sm uppercase tracking-widest text-white/50">Designeri</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <p className="font-['Cormorant_Garamond'] text-5xl md:text-6xl text-white mb-2">100%</p>
              <p className="font-['Manrope'] text-sm uppercase tracking-widest text-white/50">Dedicare</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <AnimatedSection>
            <motion.p variants={fadeInUp} className="font-['Manrope'] text-sm uppercase tracking-[0.3em] text-[#737373] mb-4">
              Recenzii
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-[#1a1a1a] mb-16">
              Ce spun clientii nostri
            </motion.h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-[#f8f8f8]"
              >
                <p className="font-['Cormorant_Garamond'] text-5xl text-[#1a1a1a]/10 mb-4">"</p>
                <p className="font-['Manrope'] text-[#737373] leading-relaxed mb-6">{testimonial.text}</p>
                <div>
                  <p className="font-['Cormorant_Garamond'] text-lg text-[#1a1a1a]">{testimonial.name}</p>
                  {testimonial.company && (
                    <p className="font-['Manrope'] text-sm text-[#737373]">{testimonial.company}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16 text-center">
          <AnimatedSection>
            <motion.h2 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl md:text-5xl text-white mb-8">
              Pregatit sa transformi spatiul tau?
            </motion.h2>
            <motion.div variants={fadeInUp}>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#1a1a1a] font-['Manrope'] text-sm uppercase tracking-widest hover:bg-[#f5f5f5] transition-colors"
              >
                Consultatie gratuita <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0a0a0a]">
        <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Link to="/" className="font-['Cormorant_Garamond'] text-xl text-white">
              MOST <span className="font-normal">WANTED</span> DESIGN
            </Link>
            <p className="font-['Manrope'] text-sm text-white/60">© 2025 Most Wanted Design. Toate drepturile rezervate.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
